import React, { useEffect, useState } from 'react';
import { View, Text, ScrollView, TouchableOpacity, ActivityIndicator, Platform } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { getFirestore, doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { useTheme } from '../contexts/ThemeContext';
import { useUser } from '../components/LoginPersistence';
import { readPathSegmentAfter } from '../utils/webRouteParams';

export default function SharedTourView({ route }) {
  const tourId = route?.params?.tourId || readPathSegmentAfter('tour');
  const { theme, isDarkMode } = useTheme();
  const { user: userData } = useUser();
  const navigation = useNavigation();

  const [tour, setTour] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!tourId) { setError('No tour ID provided.'); setLoading(false); return; }
    (async () => {
      try {
        const snap = await getDoc(doc(getFirestore(), 'sharedTours', tourId));
        if (!snap.exists()) { setError('This tour link is invalid or has been removed.'); return; }
        setTour(snap.data());
      } catch (e) {
        setError('Failed to load tour: ' + e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [tourId]);

  const saveToMyTours = async () => {
    if (!userData?.uid) { navigation.navigate('Login', { returnTo: 'SharedTourView', returnParams: { tourId } }); return; }
    setSaving(true);
    try {
      const db = getFirestore();
      const newFolderId = `shared_${tourId}_${Date.now()}`;
      const userRef = doc(db, 'users', userData.uid);
      const userSnap = await getDoc(userRef);
      const existing = (userSnap.exists() && userSnap.data().folders) || {};
      await setDoc(userRef, {
        folders: {
          ...existing,
          [newFolderId]: {
            id: newFolderId,
            name: `${tour.tourName || 'Tour'} (shared)`,
            createdAt: serverTimestamp(),
            tour: {
              name: tour.tourName || 'Tour',
              days: tour.days || [],
            },
          },
        },
      }, { merge: true });
      setSaved(true);
    } catch (e) {
      console.warn('Save tour error:', e.message);
    } finally {
      setSaving(false);
    }
  };

  const bg = isDarkMode ? '#0d0f1a' : '#f7f8fc';
  const card = isDarkMode ? 'rgba(255,255,255,0.05)' : '#ffffff';
  const border = isDarkMode ? 'rgba(255,255,255,0.09)' : 'rgba(0,0,0,0.09)';
  const textPrimary = isDarkMode ? 'rgba(255,255,255,0.92)' : '#111';
  const textSecondary = isDarkMode ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)';

  if (loading) return (
    <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: bg }}>
      <ActivityIndicator size="large" color="#e67e22" />
    </View>
  );

  if (error) return (
    <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: bg, padding: 32 }}>
      <Ionicons name="warning-outline" size={40} color="#e74c3c" />
      <Text style={{ fontSize: 16, color: textPrimary, marginTop: 16, textAlign: 'center' }}>{error}</Text>
      <TouchableOpacity onPress={() => navigation.navigate('Home')} style={{ marginTop: 20, paddingVertical: 12, paddingHorizontal: 24, backgroundColor: '#e67e22', borderRadius: 12 }}>
        <Text style={{ color: '#fff', fontWeight: '700', fontSize: 14 }}>Go Home</Text>
      </TouchableOpacity>
    </View>
  );

  const days = tour?.days || [];

  return (
    <View style={{ flex: 1, backgroundColor: bg }}>
      {/* Header */}
      <View style={{ backgroundColor: isDarkMode ? 'rgba(12,14,28,0.97)' : '#fff', paddingTop: Platform.OS === 'ios' ? 52 : 24, paddingBottom: 16, paddingHorizontal: 20, borderBottomWidth: 1, borderBottomColor: border }}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 12 }}>
          <Ionicons name="arrow-back" size={18} color={textSecondary} />
          <Text style={{ fontSize: 13, color: textSecondary, marginLeft: 6 }}>Back</Text>
        </TouchableOpacity>
        <Text style={{ fontSize: 22, fontWeight: '800', color: textPrimary, letterSpacing: 0.2 }}>
          {tour?.tourName || 'Shared Tour'}
        </Text>
        <Text style={{ fontSize: 13, color: textSecondary, marginTop: 4 }}>
          Shared by {tour?.ownerName || 'Anonymous'} · {days.length} day{days.length !== 1 ? 's' : ''}
        </Text>
      </View>

      <ScrollView contentContainerStyle={{ padding: 16, gap: 12 }}>
        {days.map((day, dayIdx) => {
          const venues = day.venues || [];
          return (
            <View key={dayIdx} style={{ backgroundColor: card, borderRadius: 14, padding: 14, borderWidth: 1, borderColor: border }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <Text style={{ fontSize: 14, fontWeight: '700', color: textPrimary }}>
                  Day {dayIdx + 1}{day.name ? ` · ${day.name}` : ''}
                </Text>
                {day.scheduledDate && (
                  <Text style={{ fontSize: 12, color: textSecondary }}>{day.scheduledDate}</Text>
                )}
              </View>
              {venues.length === 0 ? (
                <Text style={{ fontSize: 13, color: textSecondary, fontStyle: 'italic' }}>No venues</Text>
              ) : venues.map((venue, vi) => (
                <View key={vi} style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 10, paddingVertical: 6, borderTopWidth: vi > 0 ? 1 : 0, borderTopColor: border }}>
                  <View style={{ width: 24, height: 24, borderRadius: 12, backgroundColor: '#e67e22', alignItems: 'center', justifyContent: 'center', marginTop: 1 }}>
                    <Text style={{ fontSize: 11, fontWeight: '700', color: '#fff' }}>{vi + 1}</Text>
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={{ fontSize: 14, fontWeight: '600', color: textPrimary }}>{venue.name || 'Unnamed venue'}</Text>
                    <Text style={{ fontSize: 12, color: textSecondary, marginTop: 1 }}>
                      {[venue.city, venue.state].filter(Boolean).join(', ')}
                    </Text>
                    {venue.affiliate && (
                      <Text style={{ fontSize: 11, color: textSecondary, marginTop: 2 }}>{venue.affiliate}</Text>
                    )}
                  </View>
                </View>
              ))}
            </View>
          );
        })}

        {/* Actions */}
        <View style={{ gap: 10, marginTop: 8 }}>
          {saved ? (
            <View style={{ paddingVertical: 14, backgroundColor: 'rgba(46,204,113,0.12)', borderRadius: 13, alignItems: 'center', borderWidth: 1, borderColor: 'rgba(46,204,113,0.35)' }}>
              <Text style={{ fontSize: 14, fontWeight: '700', color: '#2ecc71' }}>Saved to My Tours</Text>
            </View>
          ) : (
            <TouchableOpacity
              onPress={saveToMyTours}
              disabled={saving}
              style={{ paddingVertical: 14, backgroundColor: '#2ecc71', borderRadius: 13, alignItems: 'center', opacity: saving ? 0.7 : 1 }}
            >
              {saving
                ? <ActivityIndicator color="#fff" />
                : <Text style={{ fontSize: 14, fontWeight: '700', color: '#fff' }}>Save to My Tours</Text>
              }
            </TouchableOpacity>
          )}
          <TouchableOpacity
            onPress={() => navigation.navigate('TourPlanner')}
            style={{ paddingVertical: 14, backgroundColor: '#e67e22', borderRadius: 13, alignItems: 'center' }}
          >
            <Text style={{ fontSize: 14, fontWeight: '700', color: '#fff' }}>Open Tour Planner</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </View>
  );
}
